// ============================================================
// 迷宫生成器 - 递归回溯算法
// ============================================================
import { CELL_SIZE, WALL_HEIGHT, WALL_THICKNESS } from '../config';
import * as THREE from 'three';

export interface MazeData {
  gridSize: number;
  /** 墙壁数据：maze[row][col] 的4个方向 [N,E,S,W] true=墙 */
  walls: boolean[][][];
  /** 单元格世界坐标中心 */
  cellToWorld: (row: number, col: number) => THREE.Vector3;
}

/**
 * 递归回溯算法生成迷宫
 * @param gridSize 网格尺寸（N×N）
 * @param seed 随机种子
 */
export function generateMaze(gridSize: number, seed: number): MazeData {
  // walls[row][col] = [north, east, south, west]
  const walls: boolean[][][] = Array.from({ length: gridSize }, () =>
    Array.from({ length: gridSize }, () => [true, true, true, true]),
  );

  const visited: boolean[][] = Array.from({ length: gridSize }, () =>
    Array(gridSize).fill(false),
  );

  // 种子随机数
  let state = seed;
  const rng = (): number => {
    state = (state * 16807 + 0) % 2147483647;
    return state / 2147483647;
  };

  // 方向: [北, 东, 南, 西] -> [dr, dc]
  const dirs = [
    [-1, 0],
    [0, 1],
    [1, 0],
    [0, -1],
  ];

  function shuffle<T>(arr: T[]): T[] {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  function carve(row: number, col: number): void {
    visited[row][col] = true;
    const indices = shuffle([0, 1, 2, 3]);

    for (const dirIdx of indices) {
      const [dr, dc] = dirs[dirIdx];
      const nr = row + dr;
      const nc = col + dc;
      if (nr < 0 || nr >= gridSize || nc < 0 || nc >= gridSize) continue;
      if (visited[nr][nc]) continue;

      // 打通当前格子和邻居之间的墙
      walls[row][col][dirIdx] = false;
      // 打通邻居相反方向的墙
      walls[nr][nc][(dirIdx + 2) % 4] = false;
      carve(nr, nc);
    }
  }

  // 从 (0,0) 开始
  carve(0, 0);

  // 打开入口（左下角）和出口（右上角）
  // 入口：底部边界 (gridSize-1, 0) 打开南墙
  walls[gridSize - 1][0][2] = false;
  // 出口：顶部边界 (0, gridSize-1) 打开北墙
  walls[0][gridSize - 1][0] = false;

  const cellToWorld = (row: number, col: number): THREE.Vector3 => {
    return new THREE.Vector3(
      col * CELL_SIZE + CELL_SIZE / 2,
      0,
      row * CELL_SIZE + CELL_SIZE / 2,
    );
  };

  return { gridSize, walls, cellToWorld };
}

/**
 * 根据迷宫数据创建墙壁3D网格
 */
export function createMazeMesh(maze: MazeData): THREE.Group {
  const group = new THREE.Group();
  const wallMaterial = new THREE.MeshStandardMaterial({
    color: '#c4b9a6',
    roughness: 0.6,
    metalness: 0.05,
  });

  const accentMaterial = new THREE.MeshStandardMaterial({
    color: '#b0a590',
    roughness: 0.5,
    metalness: 0.08,
  });

  const halfCell = CELL_SIZE / 2;
  const halfThick = WALL_THICKNESS / 2;
  const wallGeoH = new THREE.BoxGeometry(CELL_SIZE + WALL_THICKNESS, WALL_HEIGHT, WALL_THICKNESS);
  const wallGeoV = new THREE.BoxGeometry(WALL_THICKNESS, WALL_HEIGHT, CELL_SIZE + WALL_THICKNESS);
  const pillarGeo = new THREE.BoxGeometry(WALL_THICKNESS, WALL_HEIGHT + 0.4, WALL_THICKNESS);

  const { gridSize, walls } = maze;

  for (let row = 0; row < gridSize; row++) {
    for (let col = 0; col < gridSize; col++) {
      const cx = col * CELL_SIZE + halfCell;
      const cz = row * CELL_SIZE + halfCell;

      // 北墙 (z-)
      if (walls[row][col][0]) {
        const w = new THREE.Mesh(wallGeoH, wallMaterial);
        w.position.set(cx, WALL_HEIGHT / 2, cz - halfCell);
        w.castShadow = true;
        w.receiveShadow = true;
        group.add(w);
      }

      // 东墙 (x+)
      if (walls[row][col][1]) {
        const w = new THREE.Mesh(wallGeoV, wallMaterial);
        w.position.set(cx + halfCell, WALL_HEIGHT / 2, cz);
        w.castShadow = true;
        w.receiveShadow = true;
        group.add(w);
      }

      // 南墙 (z+)
      if (walls[row][col][2]) {
        const w = new THREE.Mesh(wallGeoH, wallMaterial);
        w.position.set(cx, WALL_HEIGHT / 2, cz + halfCell);
        w.castShadow = true;
        w.receiveShadow = true;
        group.add(w);
      }

      // 西墙 (x-)
      if (walls[row][col][3]) {
        const w = new THREE.Mesh(wallGeoV, wallMaterial);
        w.position.set(cx - halfCell, WALL_HEIGHT / 2, cz);
        w.castShadow = true;
        w.receiveShadow = true;
        group.add(w);
      }

      // 四角柱子
      for (const [ox, oz] of [
        [-halfCell, -halfCell],
        [halfCell, -halfCell],
        [-halfCell, halfCell],
        [halfCell, halfCell],
      ]) {
        const pillar = new THREE.Mesh(pillarGeo, accentMaterial);
        pillar.position.set(cx + ox, WALL_HEIGHT / 2 + 0.2, cz + oz);
        pillar.castShadow = true;
        pillar.receiveShadow = true;
        group.add(pillar);
      }
    }
  }

  // 外边界墙（迷宫外围，入口和出口留缺口）
  const boundaryW = gridSize * CELL_SIZE;
  const boundaryMat = new THREE.MeshStandardMaterial({
    color: '#9e9078',
    roughness: 0.45,
    metalness: 0.1,
  });

  const entryGapStart = 0;
  const entryGapEnd = CELL_SIZE;
  const exitGapStart = boundaryW - CELL_SIZE;
  const exitGapEnd = boundaryW;

  // 南边界墙（入口缺口在左下角 x: 0~CELL_SIZE）
  const southLeftGeo = new THREE.BoxGeometry(entryGapStart, WALL_HEIGHT, WALL_THICKNESS);
  const southLeft = new THREE.Mesh(southLeftGeo, boundaryMat);
  southLeft.position.set(entryGapStart / 2, WALL_HEIGHT / 2, boundaryW - halfThick);
  southLeft.castShadow = true; southLeft.receiveShadow = true;
  group.add(southLeft);

  const southRightGeo = new THREE.BoxGeometry(boundaryW - entryGapEnd, WALL_HEIGHT, WALL_THICKNESS);
  const southRight = new THREE.Mesh(southRightGeo, boundaryMat);
  southRight.position.set(entryGapEnd + (boundaryW - entryGapEnd) / 2, WALL_HEIGHT / 2, boundaryW - halfThick);
  southRight.castShadow = true; southRight.receiveShadow = true;
  group.add(southRight);

  // 北边界墙（出口缺口在右上角 x: exitGapStart~exitGapEnd）
  const northLeftGeo = new THREE.BoxGeometry(exitGapStart, WALL_HEIGHT, WALL_THICKNESS);
  const northLeft = new THREE.Mesh(northLeftGeo, boundaryMat);
  northLeft.position.set(exitGapStart / 2, WALL_HEIGHT / 2, -halfThick);
  northLeft.castShadow = true; northLeft.receiveShadow = true;
  group.add(northLeft);

  const northRightGeo = new THREE.BoxGeometry(boundaryW - exitGapEnd, WALL_HEIGHT, WALL_THICKNESS);
  const northRight = new THREE.Mesh(northRightGeo, boundaryMat);
  northRight.position.set(exitGapEnd + (boundaryW - exitGapEnd) / 2, WALL_HEIGHT / 2, -halfThick);
  northRight.castShadow = true; northRight.receiveShadow = true;
  group.add(northRight);

  // 西边界墙（完整）
  const westGeo = new THREE.BoxGeometry(WALL_THICKNESS, WALL_HEIGHT, boundaryW);
  const west = new THREE.Mesh(westGeo, boundaryMat);
  west.position.set(-halfThick, WALL_HEIGHT / 2, boundaryW / 2);
  west.castShadow = true; west.receiveShadow = true;
  group.add(west);

  // 东边界墙（完整）
  const eastGeo = new THREE.BoxGeometry(WALL_THICKNESS, WALL_HEIGHT, boundaryW);
  const east = new THREE.Mesh(eastGeo, boundaryMat);
  east.position.set(boundaryW - halfThick, WALL_HEIGHT / 2, boundaryW / 2);
  east.castShadow = true; east.receiveShadow = true;
  group.add(east);

  // 入口和出口标记（在地板上）
  const markerMat = new THREE.MeshStandardMaterial({
    color: '#44aa44',
    roughness: 0.4,
    metalness: 0.3,
    emissive: '#114411',
    emissiveIntensity: 0.5,
  });
  const exitMarkerMat = new THREE.MeshStandardMaterial({
    color: '#ffaa33',
    roughness: 0.3,
    metalness: 0.5,
    emissive: '#331100',
    emissiveIntensity: 0.6,
  });

  // 入口地板标记
  const entryMarker = new THREE.Mesh(
    new THREE.PlaneGeometry(CELL_SIZE * 0.6, CELL_SIZE * 0.3),
    markerMat,
  );
  entryMarker.rotation.x = -Math.PI / 2;
  entryMarker.position.set(CELL_SIZE / 2, 0.02, boundaryW - 0.05);
  group.add(entryMarker);

  // 出口地板标记
  const exitMarker = new THREE.Mesh(
    new THREE.PlaneGeometry(CELL_SIZE * 0.6, CELL_SIZE * 0.3),
    exitMarkerMat,
  );
  exitMarker.rotation.x = -Math.PI / 2;
  exitMarker.position.set(boundaryW - CELL_SIZE / 2, 0.02, 0.05);
  group.add(exitMarker);

  return group;
}

/**
 * 碰撞检测数据：提取所有墙壁的AABB
 */
export interface WallAABB {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

export function buildWallColliders(maze: MazeData): WallAABB[] {
  const colliders: WallAABB[] = [];
  const halfCell = CELL_SIZE / 2;
  const halfThick = WALL_THICKNESS / 2;
  const { gridSize, walls } = maze;

  for (let row = 0; row < gridSize; row++) {
    for (let col = 0; col < gridSize; col++) {
      const cx = col * CELL_SIZE + halfCell;
      const cz = row * CELL_SIZE + halfCell;

      // 北墙
      if (walls[row][col][0]) {
        colliders.push({
          minX: cx - halfCell - halfThick,
          maxX: cx + halfCell + halfThick,
          minZ: cz - halfCell - halfThick,
          maxZ: cz - halfCell + halfThick,
        });
      }
      // 东墙
      if (walls[row][col][1]) {
        colliders.push({
          minX: cx + halfCell - halfThick,
          maxX: cx + halfCell + halfThick,
          minZ: cz - halfCell - halfThick,
          maxZ: cz + halfCell + halfThick,
        });
      }
      // 南墙
      if (walls[row][col][2]) {
        colliders.push({
          minX: cx - halfCell - halfThick,
          maxX: cx + halfCell + halfThick,
          minZ: cz + halfCell - halfThick,
          maxZ: cz + halfCell + halfThick,
        });
      }
      // 西墙
      if (walls[row][col][3]) {
        colliders.push({
          minX: cx - halfCell - halfThick,
          maxX: cx - halfCell + halfThick,
          minZ: cz - halfCell - halfThick,
          maxZ: cz + halfCell + halfThick,
        });
      }
    }
  }

  // 外边界（4面墙，入口/出口留缺口）
  const bw = gridSize * CELL_SIZE;
  const entryGapStart = 0;
  const entryGapEnd = CELL_SIZE;
  const exitGapStart = bw - CELL_SIZE;
  const exitGapEnd = bw;

  // 北边界（出口处留缺口 x: exitGapStart~exitGapEnd）
  colliders.push({ minX: 0, maxX: exitGapStart, minZ: -halfThick, maxZ: halfThick });
  colliders.push({ minX: exitGapEnd, maxX: bw, minZ: -halfThick, maxZ: halfThick });
  // 南边界（入口处留缺口 x: entryGapStart~entryGapEnd）
  colliders.push({ minX: 0, maxX: entryGapStart, minZ: bw - halfThick, maxZ: bw + halfThick });
  colliders.push({ minX: entryGapEnd, maxX: bw, minZ: bw - halfThick, maxZ: bw + halfThick });
  // 西边界（完整）
  colliders.push({ minX: -halfThick, maxX: halfThick, minZ: 0, maxZ: bw });
  // 东边界（完整）
  colliders.push({ minX: bw - halfThick, maxX: bw + halfThick, minZ: 0, maxZ: bw });

  return colliders;
}
